import { SEARCH_DATA_KEY } from 'api/search'
import { useQueryClient } from '@tanstack/react-query'
import { useSaveAndSyncDeal } from 'api/aws/organizations_table/useSaveAndSyncDeal'
import { useUpdateCache } from 'api/aws/organizations_table/useUpdateCache'
import { useUpdateFields } from 'api/aws/organizations_table/useUpdateFields'
import useTotals from 'api/customHooks/useTotals'
import dayjs from 'dayjs'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { calculateProductStatus } from 'utils/dealCalculations'
import { v4 as uuidv4 } from 'uuid' // For generating unique note IDs
import { useStrategy } from '../../api/aws/useStrategy'
import { useUpdateDeal } from '../../api/aws/useEntityUpdate'
import { useUpdateItems } from '../../api/aws/useUpdateItems'
import { useCreateDocument } from '../../api/PandaDoc/useCreateDocument'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useNotifyApprovers } from '../../api/slack/useNotifyApprovers'
import { createApproversNotification } from '../../components/Notifications/createNotifyPayload'
import { updateContractTimelineData } from '../../components/Timeline/createTimelineData'
import { calculateMonthsBetween } from '../../shared/calculations'
import { calculateProductsDateRange } from '../../utils/dealCalculations'
import { useSnackbarContext } from '../SnackbarProvider/SnackbarProvider'
import { getApprovalBatchReplaceItem, getDealBatchReplaceItems } from './dealUpdateData'
import { useDetermineDealState } from './determineDealState'
import { generateUpdatedDealState } from './generateUpdatedDealState'
import { getProductBatchReplaceItems } from './getProductRowChanges'
import { normalizeDeal, normalizeProductRowOnLoad, updateDealStatus } from './normalizeDeal'

const paidProductsMargins = {
  'Meta Advertising': 40,
  'Linkedin Advertising': 40,
  'Spotify Advertising': 40,
  'TikTok Advertising': 40,
  'Youtube Advertising': 70,
  'Paid Search Advertising': 30,
  'Google Display Advertising': 70,
}
const _draftStatuses = [
  'Draft',
  'Saved',
  'Created',
  'Requested',
  'Rejected',
  'Resolved',
  'Approved',
  'Approval Requested',
  'Updated',
  'Modified',
  'Needs Approval',
  'Completed Internally',
]
const initialValidationState = {
  targetLaunchDate: false,
  dependenciesDueDate: false,
  authorizedRecipient: false,
  contactRecipient: false,
  recipients: false,
  products: false,
  startDate: false,
  endDate: false,
}
// Create a context for the deal
const DealContext = createContext<any>({})

export const useDealDetailsContext = () => useContext(DealContext)

export const DealDetailsProvider = ({ children, dealData, relatedProducts }) => {
  const queryClient = useQueryClient()
  const { updateOrgCacheAfterDealSave: _updateOrgCacheAfterDealSave } = useUpdateCache()
  const [isDealStatusChange, setIsDealStatusChange] = useState(false)
  const { determineFirstSaveStage, determineIsDraft } = useDetermineDealState()
  const { mutate: sendApproverNotification } = useNotifyApprovers()
  const { totalMonths, allSame } = dealData ? calculateProductsDateRange(dealData.products || []) : { totalMonths: 1, allSame: false }
  const [totalGrossProfit, setTotalGrossProfit] = useState(
    dealData && dealData.grossProfit && totalMonths ? dealData.grossProfit * totalMonths : 0
  )
  const [dealFieldChanges, setDealFieldChanges] = useState(null)
  const [productRowChanges, setProductRowChanges] = useState([])
  const [deletedProductIds, setDeletedProductIds] = useState([])

  const [originalProductRows, setOriginalProductRows] = useState([])
  const [productRows, setProductRows] = useState([])
  const [isEditMode, setIsEditMode] = useState(false)
  const [viewMode, setViewMode] = useState('strategy')
  const [searchCampaigns, setSearchCampaigns] = useState([])
  const [socialCampaigns, setSocialCampaigns] = useState([])
  const [socialAds, setSocialAds] = useState([])
  const [displayCampaigns, setDisplayCampaigns] = useState([])
  const [originalDeal, setOriginalDeal] = useState({
    ...dealData,
    dealStatus: 'In Progress',
    typeChange: false,
  })
  const [newDeal, setNewDeal] = useState({
    ...dealData,
    dealStatus: 'In Progress',
  })
  const totals = useTotals(productRows, newDeal?.implementationFee, newDeal?.type)
  const [dealId, setDealId] = useState(newDeal ? newDeal.deal_uuid : '')
  const { strategy: currentStrategy } = useStrategy(dealId)
  const [strategy, setStrategy] = useState(null) // State for strategy data
  const { activeUser } = useActiveUser()
  const [timelineData, setTimelineData] = useState(null)
  const [clientMargin, setClientMargin] = useState(0)
  const [hasProducts, setHasProducts] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [ncoDrawerOpen, setNcoDrawerOpen] = useState(false)
  const [isGridDrawerOpen, setIsGridDrawerOpen] = useState(false)
  ///////////////////////////////////////////////
  //////// Approval State ///////
  const [approvalState, setApprovalState] = useState({
    hasProducts: productRows?.length > 0,
    needsApproval: newDeal?.needsApproval ?? false,
    status: newDeal?.status ?? 'Draft',
    stage: newDeal?.stage ?? 'Discovery',
    isApproved: newDeal?.isApproved ?? false,
    canCreateContract: newDeal?.canCreateContract ?? false,
    isCompleted: newDeal?.isCompleted ?? false,
  })

  const [originalApprovalState, setOriginalApprovalState] = useState({
    hasProducts: productRows?.length > 0,
    needsApproval: originalDeal?.needsApproval ?? false,
    status: originalDeal?.status ?? 'Draft',
    stage: originalDeal?.stage ?? 'Discovery',
    isRequested: originalDeal.isRequested ?? false,
    isApproved: originalDeal?.isApproved ?? false,
    canCreateContract: originalDeal?.canCreateContract ?? false,
    isCompleted: newDeal?.isCompleted ?? false,
  })
  ////////////////////////////
  const { mutate: updateItems } = useUpdateItems()
  const { mutate: createDocument } = useCreateDocument()
  const { mutate: updateDeal } = useUpdateDeal()
  const { mutate: updateFields } = useUpdateFields()
  const { showSnackbar } = useSnackbarContext()
  const [isCreating, setIsCreating] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [errorKeys, setErrorKeys] = useState([])
  const [validationErrors, setValidationErrors] = useState(initialValidationState)
  const hasInitializedProducts = useRef(false)
  const { mutateAsync: saveDealAsync } = useSaveAndSyncDeal()

  // calculate totals
  const calculateMediaSpend = (price, marginPercentage) => {
    const marginValue = parseFloat(price) * (parseFloat(marginPercentage) / 100)
    const newMediaSpend = (parseFloat(price) - marginValue).toFixed(2)
    return newMediaSpend
  }

  const calculateTotalForRow = row => {
    // If the payer is 'Client', use the billed price
    if (row.payer === 'Client') {
      return parseFloat(row.billedPrice || 0)
    }

    // Otherwise, use the standard calculation for price * quantity
    return parseFloat(row.price || 0) * parseFloat(row.qty || 1)
  }

  const computeTotals = (deal, productRows) => {
    const grossProfit = productRows.reduce((acc, row) => acc + parseFloat(row.grossProfit || 0), 0)
    const monthlyTotal = productRows.reduce((acc, row) => acc + parseFloat(row.total || 0), 0)
    const implementationFee = parseFloat(deal?.implementationFee || 0)
    const total = monthlyTotal
    return { grossProfit, monthlyTotal, implementationFee, total, totalGrossProfit }
  }
  // Validation
  const validateFields = (deal, products) => {
    // Ensure recipients object exists with default empty arrays
    const recipients = deal?.recipients || { authorizedRecipient: [], contactRecipient: [] }
    const authorizedRecipientError = !(
      Array.isArray(recipients.authorizedRecipient) && recipients.authorizedRecipient.length > 0
    )
    const contactRecipientError = !(
      Array.isArray(recipients.contactRecipient) && recipients.contactRecipient.length > 0
    )
    const errors = {
      targetLaunchDate: !deal?.targetLaunchDate,
      dependenciesDueDate: !deal?.dependenciesDueDate,
      authorizedRecipient: authorizedRecipientError,
      contactRecipient: contactRecipientError,
      recipients: authorizedRecipientError || contactRecipientError,
      products: products
        ? products.some(product => !product.productName) || !products?.length
        : productRows.some(product => !product.productName) || !productRows?.length,
      startDate: !deal?.startDate,
      endDate: !deal?.endDate,
      // saveRequired: productRowChanges?.length > 0 || deletedProductIds?.length > 0,
    }
    const keys = Object.entries(errors)
      .map(([key, value]) => {
        if (value) {
          return key
        }
      })
      .filter(Boolean)
    setErrorKeys(keys)
    setValidationErrors(errors)
    return !Object.values(errors).some(Boolean) // Return whether any of the errors are true
  }
  const handleSaveAndSync = async () => {
    const { PK, SK, deal_uuid } = newDeal
    const valid = validateFields(newDeal, productRows)
    if (!valid) {
      showSnackbar('Please fix validation errors before saving.', 'error')
      return
    }
    // const statusUpdate = determineFirstSaveStatus(newDeal, productRows)
    // console.log('statusUpdate', statusUpdate)
    const stageUpdate = determineFirstSaveStage(newDeal, productRows)
    console.log('stageUpdate', stageUpdate)
    const isDraftUpdate = determineIsDraft(newDeal, productRows)
    console.log('isDraftUpdate', isDraftUpdate)
    let dealUpdates = [...dealFieldChanges]

    // 🧠 Check for computed dealStatus changes even if no direct deal edits
    const computedDealStatus = updateDealStatus(newDeal, productRows)
    const originalStatus = originalDeal?.dealStatus || ''
    const newStatus = computedDealStatus?.newValue || ''
    const needsStatusUpdate = newStatus && newStatus !== originalStatus

    // 🧠 Add a minimal dealUpdate if only dealStatus needs updating
    if (dealUpdates.length === 0 && needsStatusUpdate) {
      dealUpdates.push({
        PK,
        SK,
        tableName: 'ORGANIZATIONS_TABLE',
        newItem: {
          ...newDeal, // keep current state
          EntityType: 'deal',
          dealStatus: newStatus,
          totals,
        },
      })
    }

    // Merge calculated fields into the `newItem` of the first deal update
    if (dealUpdates.length > 0) {
      const firstUpdate = dealUpdates[0]
      if (firstUpdate.newItem) {
        firstUpdate.newItem.totals = totals
        if (stageUpdate?.newValue) firstUpdate.newItem.stage = stageUpdate.newValue
        if (isDraftUpdate?.newValue !== undefined && newDeal?.status !== 'Completed Internally')
          firstUpdate.newItem.isDraft = isDraftUpdate.newValue
        if (needsStatusUpdate) firstUpdate.newItem.dealStatus = newStatus
      }
    }
    console.log('dealUpdates', dealUpdates)
    try {
      await saveDealAsync(
        {
          PK,
          SK,
          dealUpdates: dealUpdates,
          // productUpdates: productRowChanges,
          deletedProducts: deletedProductIds,
          products: productRows,
          deal_uuid: newDeal?.deal_uuid,
        },
        {
          onSuccess: (data, variables) => {
            clearChangeTracking() // ✅ FIRST: clear state
            // updateOrgCacheAfterDealSave(queryClient, variables)
            validateFields(newDeal, productRows)
            showSnackbar('Deal saved.', 'success')
          },
        }
      )
      setOriginalDeal(newDeal) // optional: update originalDeal to match saved version
    } catch (error) {
      clearChangeTracking()
      showSnackbar('Failed to save deal.', 'error')
      console.error('Error saving deal:', error)
    }
  }
  const handleSaveOnly = async () => {
    console.log('handleSaveOnly')
    const { PK, SK, deal_uuid } = newDeal
    const valid = validateFields(newDeal, productRows)
    // const statusUpdate = determineFirstSaveStatus(newDeal, productRows)
    const stageUpdate = determineFirstSaveStage(newDeal, productRows)
    const isDraftUpdate = determineIsDraft(newDeal, productRows)
    let dealUpdates = [...dealFieldChanges]

    // 🧠 Check for computed dealStatus changes even if no direct deal edits
    const computedDealStatus = updateDealStatus(newDeal, productRows)
    const originalStatus = originalDeal?.dealStatus || ''
    const newStatus = computedDealStatus?.newValue || ''
    const needsStatusUpdate = newStatus && newStatus !== originalStatus

    // 🧠 Add a minimal dealUpdate if only dealStatus needs updating
    if (dealUpdates.length === 0 && needsStatusUpdate) {
      dealUpdates.push({
        PK,
        SK,
        tableName: 'ORGANIZATIONS_TABLE',
        newItem: {
          ...newDeal, // keep current state
          EntityType: 'deal',
          dealStatus: newStatus,
          totals,
        },
      })
    }

    // 💡 Merge in stage/status/draft/totals to the first update
    if (dealUpdates.length > 0) {
      const firstUpdate = dealUpdates[0]
      if (firstUpdate.newItem) {
        firstUpdate.newItem.totals = totals
        if (stageUpdate?.newValue) firstUpdate.newItem.stage = stageUpdate.newValue
        if (isDraftUpdate?.newValue !== undefined) firstUpdate.newItem.isDraft = isDraftUpdate.newValue
        // if (statusUpdate?.newValue) firstUpdate.newItem.status = statusUpdate.newValue
        if (needsStatusUpdate) firstUpdate.newItem.dealStatus = newStatus
      }
    }

    console.log('dealUpdates', dealUpdates)

    try {
      await saveDealAsync(
        {
          PK,
          SK,
          dealUpdates: dealUpdates,
          deletedProducts: deletedProductIds,
          products: productRows,
          deal_uuid: newDeal?.deal_uuid,
        },
        {
          onSuccess: (data, variables) => {
            clearChangeTracking()
            // updateOrgCacheAfterDealSave(queryClient, variables)
            queryClient.invalidateQueries({ queryKey: ['item', newDeal?.PK, newDeal?.SK] })
            const validAfterSave = validateFields(newDeal, productRows)
            setIsValid(validAfterSave)
            showSnackbar('Deal and products saved successfully.', 'success')
          },
        }
      )
      setOriginalDeal(newDeal)
      setDeletedProductIds([])
    } catch (error) {
      clearChangeTracking()
      showSnackbar('Failed to save deal.', 'error')
      console.error('Error saving deal:', error)
    }
  }

  const clearChangeTracking = () => {
    setDealFieldChanges([])
    setProductRowChanges([])
    setDeletedProductIds([])
  }

  const handleUpdateDeal = async (_PK, _SK, updates, onSuccess, onError) => {
    setIsCreating(true)
    updateFields(
      {
        updates,
      },
      {
        onSuccess: data => {
          setIsCreating(false)
          showSnackbar('Deal updated successfully', 'success')
          onSuccess && onSuccess(data)
        },
        onError: error => {
          setIsCreating(false)
          setIsError(true)
          showSnackbar('Error updating deal', 'error')
          onError && onError(error)
        },
      }
    )
  }
  // Product Row Functions
  const addRow = async scrollRef => {
    setHasProducts(true)

    const startDate = newDeal.startDate ? dayjs(newDeal.startDate) : null
    const endDate = newDeal.endDate ? dayjs(newDeal.endDate) : null
    const totalMonthsForDeal = calculateMonthsBetween(startDate, endDate)
    const newInstanceId = uuidv4()
    const newRowId = productRows.length + 1
    const newRow = {
      PK: newDeal.PK, // e.g., "ORG_c7982d02-753c-4deb-94fc-8726729402eb"
      SK: `PRODUCT_${newInstanceId}`,
      deal_uuid: newDeal.deal_uuid,
      instanceId: newInstanceId,
      rowId: newInstanceId,
      addtlDescriptor: '',
      qty: totalMonthsForDeal ? totalMonthsForDeal : '1.00',
      EntityType: 'product',
      price: 0.0,
      billedPrice: 0.0,
      margin: 0.0,
      mediaSpend: 0.0,
      total: 0.0,
      netTotal: 0 * (totalMonthsForDeal || 1),
      status: '',
      dateRange: [newDeal.startDate, newDeal.endDate],
      startDate: newDeal.startDate,
      endDate: newDeal.endDate,
      payer: 'GRO',
      accountUsed: 'GRO',
      isNew: true, // Optional flag to identify this as a new row
    }
    setProductRows(prevRows => {
      const updatedRows = [...prevRows, newRow]

      // setNewDeal(prevDetails => ({
      //   ...prevDetails,
      //   hasProducts: true,
      // }))
      // Optimistically push to the query cache
      queryClient.setQueryData(SEARCH_DATA_KEY, (old: any) => {
        if (!old) return old
        const products = Array.isArray(old.products) ? old.products : []
        return {
          ...old,
          products: [...products, newRow],
          // (optional) if you also mirror hasProducts anywhere at root:
          // hasProducts: true,
        }
      })

      // optional: if you still keep hasProducts locally, derive from cache instead.
      // setHasProducts(true) becomes unnecessary if you compute from products.length > 0

      // scroll to bottom after React paints the new row
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
      }, 0)

      handleProductRowChange(originalProductRows, updatedRows) // <-- ADD THIS

      return updatedRows
    })
  }
  useEffect(() => {
    if (productRows) {
      // if (productRows?.length > 0) console.log('productRows', productRows)
      validateFields(newDeal, productRows)
    }
  }, [productRows])
  const removeRow = async instanceId => {
    setProductRows(prevRows => {
      const updatedRows = prevRows.filter(row => row.instanceId !== instanceId)

      setDeletedProductIds(prev => [...prev, instanceId])

      setNewDeal(prevDetails => ({
        ...prevDetails,
        hasProducts: updatedRows.length > 0,
      }))

      handleProductRowChange(originalProductRows, updatedRows) // <-- ADD THIS HERE

      return updatedRows
    })
  }
  const calculateGrossProfit = (price, mediaSpend) => {
    return (parseFloat(price) - parseFloat(mediaSpend)).toFixed(2)
  }
  ///////////////////////////// handleProductChange ////////////////////////////
  /////////////////////////////////////////////////////////////////////////////

  const normalizeProductRow = (row, fieldChanged = '', newValue = '', clientMargin = 0) => {
    const updatedRow = { ...row, [fieldChanged]: newValue }

    // Handle date fields first (either directly or via dateRange)
    if (fieldChanged === 'dateRange') {
      const [startDate, endDate] = newValue
      updatedRow.startDate = startDate
      updatedRow.endDate = endDate
    }

    if (fieldChanged === 'startDate') {
      updatedRow.startDate = newValue
    }

    if (fieldChanged === 'endDate') {
      updatedRow.endDate = newValue
    }

    // Calculate qty and status only after date values are set
    const start = updatedRow.startDate
    const end = updatedRow.endDate
    if (start && end) {
      updatedRow.qty = calculateMonthsBetween(start, end) || 1
      updatedRow.status = calculateProductStatus(start, end)
    }

    // Handle payer logic
    if (fieldChanged === 'payer') {
      if (newValue === 'Client') {
        updatedRow.margin = clientMargin
      } else if (newValue === 'GRO') {
        updatedRow.margin = paidProductsMargins[updatedRow.productName] ?? 0
      }
    }

    // Ensure margin and price are numbers
    const parsedMargin = parseFloat(updatedRow.margin || 0)
    const parsedPrice = parseFloat(updatedRow.price || 0)

    updatedRow.mediaSpend = calculateMediaSpend(parsedPrice, parsedMargin)
    updatedRow.grossProfit = calculateGrossProfit(parsedPrice, updatedRow.mediaSpend)
    updatedRow.billedPrice =
      updatedRow.payer === 'Client' ? parseFloat(updatedRow.grossProfit).toFixed(2) : parsedPrice.toFixed(2)

    const totalMonths = calculateMonthsBetween(start, end) || 1
    updatedRow.qty = totalMonths
    updatedRow.total = calculateTotalForRow(updatedRow)
    updatedRow.netTotal = parsedPrice * totalMonths

    return updatedRow
  }

  const handleProductChange = (index, field, value, additionalUpdates = {}) => {
    const currentRow = productRows[index]
    const currentValue = currentRow?.[field]

    // 🚫 Prevent updating if value is unchanged
    if (value === currentValue && Object.keys(additionalUpdates).length === 0) return

    const updatedRows = productRows.map((row, i) => {
      if (i === index) {
        const mergedRow = { ...row, ...additionalUpdates }
        return normalizeProductRow(mergedRow, field, value, clientMargin)
      }
      return row
    })

    setProductRows(updatedRows)
    handleProductRowChange(originalProductRows, updatedRows)

    const newTimelineData = updateContractTimelineData(
      newDeal?.deal_uuid,
      activeUser,
      'Product Update',
      'productUpdate',
      'Product(s) Updated',
      `${field} : ${JSON.stringify(value)}`
    )
    setTimelineData(newTimelineData)
  }

  /////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////

  ////////////////////////// handleChange //////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////
  const handleChange = (field, newValue, organization: any = {}) => {
    console.log('handleChangeTriggered')
    try {
      // ✅ Ensure newValue is serializable (not an event or object with circular refs)
      if (typeof newValue === 'object') {
        console.log('newValue object:', newValue)
        if (newValue instanceof Event) {
          console.warn('❌ Received event instead of value in handleChange!')
          return
        }
      }
      setNewDeal(prevDetails => {
        const updatedDeal = { ...prevDetails }

        // Handle specific fields like implementationFee or recipients
        if (field === 'implementationFee') {
          updatedDeal[field] = parseFloat(newValue) || 0
        } else if (field === 'authorizedRecipient') {
          console.log('authorizedRecipient CHANGE DETECTED: ', 'NEW VALUE: ', newValue)

          const recipients = newValue.map(recipient => ({
            id: recipient.id || recipient.contacts_uuid || recipient.user_uuid,
            contact_id: recipient?.panda_contact_id || recipient?.contact_id || recipient?.id,
            email: recipient['Employee Email'] || recipient.email,
            first_name: recipient['First Name'] || recipient.firstName || recipient.first_name,
            last_name: recipient['Last Name'] || recipient.lastName || recipient.last_name,
            role: field === 'authorizedRecipient' ? 'Authorized Personnel' : 'Client',
            signing_order: field === 'authorizedRecipient' ? 1 : 2,
          }))

          updatedDeal.recipients = {
            ...updatedDeal.recipients,
            [field]: recipients,
          }
        } else if (field === 'contactRecipient') {
          console.log('contactRecipient CHANGE DETECTED: ', 'NEW VALUE: ', newValue)

          const recipients = newValue.map(recipient => ({
            id: recipient.id || recipient.contacts_uuid || recipient.user_uuid,
            contact_id: recipient?.panda_contact_id || recipient?.contact_id || recipient?.id,
            email: recipient['Employee Email'] || recipient.email,
            first_name: recipient['First Name'] || recipient.firstName || recipient.first_name,
            last_name: recipient['Last Name'] || recipient.lastName || recipient.last_name,
            role: 'Client',
            signing_order: field === 'contactRecipient' ? 1 : 2,
          }))

          updatedDeal.recipients = {
            ...updatedDeal.recipients,
            [field]: recipients,
          }
          console.log('updatedDeal', updatedDeal)
        } else if (field === 'numberOfPayments') {
          updatedDeal[field] = parseFloat(newValue) || 0
        } else if (field === 'type') {
          // Check against originalDeal.type, not newDeal.type
          if (newValue !== originalDeal.type) {
            // Type has changed, update the name, status, and approval state
            const updatedApprovalState = generateUpdatedDealState(
              productRows?.length > 0,
              updatedDeal,
              originalDeal.type,
              newValue,
              organization,
              updatedDeal,
              productRows
            )
            if ([updatedDeal?.name].includes('undefined')) {
              updatedDeal.name = `${organization?.name} - ${newValue}`
            } else {
              updatedDeal.name = `${organization?.name} - ${newValue}`
            }
            updatedDeal.dealStatus = updatedDeal.dealStatus || 'Active'
            updatedDeal.stage = updatedApprovalState.stage || 'Discovery'
            updatedDeal.status = updatedApprovalState.status || 'Draft'
            updatedDeal.type = newValue // Update the type in the deal
            updatedDeal.isApproved = false
            updatedDeal.isRequested = false
            updatedDeal.typeChange = true
            // updatedDeal.approvers = []
          } else {
            const hasProducts = productRows?.length > 0
            // Revert to original type, approval state, and deal details
            updatedDeal.name = organization ? `${organization.name} - ${originalDeal.type}` : updatedDeal.name
            updatedDeal.dealStatus = originalDeal.dealStatus
            updatedDeal.stage = hasProducts ? originalApprovalState.stage : 'Discovery'
            updatedDeal.status = hasProducts ? originalApprovalState.status : 'Draft'
            updatedDeal.type = originalDeal.type // Revert the type back to the original
            updatedDeal.typeChange = false
            updatedDeal.isApproved = originalDeal.isApproved
            updatedDeal.isRequested = originalDeal.isRequested
          }
        } else {
          // For any other field, just update the value
          updatedDeal[field] = newValue
        }

        // Validate and return updated deal
        const isValid = validateFields(updatedDeal, productRows)
        setIsValid(isValid)
        return updatedDeal
      })
      const newTimelineData = updateContractTimelineData(
        newDeal?.deal_uuid,
        activeUser,
        'Update',
        'update',
        'Field updated',
        `${field} : ${JSON.stringify(newValue, (key, value) =>
          typeof value === 'object' && value !== null ? '[Object]' : value
        )}` // 👈 Prevent circular structure
      )
      setTimelineData(newTimelineData)
    } catch (error) {
      console.error('❌ Error in handleChange:', error)
    }
  }

  /////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (!dealData) return // Early return if dealData is not defined
    // console.log('[DealDetailsProvider] Deal Data in useEffect: ', dealData)
    const normalized = normalizeDeal(dealData)
    setNewDeal(normalized)
    setOriginalDeal(normalized)
    setHasProducts(productRows?.length > 0)

    // Ensure that organizations are part of dealData and not cleared
    if (dealData.organizations) {
      // console.log('organizations detected: ', dealData.organizations)
      setNewDeal(prevDeal => ({ ...prevDeal, organizations: dealData.organizations }))
    }
    if (relatedProducts && relatedProducts?.length > 0) {
      // if (!isProductsByDealIdLoading && productsByDealId) {
      // console.log('PRODUCTS DETECTED: ', dealData.products)
      const updatedContractProducts = relatedProducts?.map((row, index) => {
        // const updatedContractProducts = productsByDealId.map((row, index) => {
        const startDate = row.startDate ? dayjs(row.startDate) : null
        const endDate = row.endDate ? dayjs(row.endDate) : null
        const dateRange = row?.dateRange ? row?.dateRange : startDate && endDate ? [startDate, endDate] : [null, null]
        const totalMonthsForProduct = calculateMonthsBetween(startDate, endDate)
        const productPrice =
          row.payer === 'Client' && row.margin === 0
            ? 0
            : row.payer === 'Client' && row.margin > 0
            ? parseFloat(row.grossProfit)
            : parseFloat(row.price)
        const grossProfit = calculateGrossProfit(row.price, row.mediaSpend)
        const billedPrice =
          row.payer === 'Client' && row.margin === 0
            ? 0
            : row.payer === 'Client' && row.margin > 0
            ? parseFloat(grossProfit)
            : parseFloat(row.price)
        return {
          ...row, // Preserve all fields in the product row, including new ones
          deal_uuid: dealData?.deal_uuid,
          instanceId: row.instanceId || uuidv4(), // Keep existing instanceId or generate a new one
          rowId: row.instanceId || uuidv4(), // Ensure rowId is present
          productName: row.productName || row.selectedProduct?.title, // Handle product name
          qty: totalMonthsForProduct || row.qty || '1.00', // Handle quantity calculation
          dateRange: dateRange,
          startDate, // Update startDate as Dayjs object
          endDate, // Update endDate as Dayjs object
          payer: row.payer || 'GRO', // Default payer
          grossProfit: grossProfit,
          billedPrice: parseFloat(String(billedPrice)).toFixed(2),
        }
      })

      setProductRows(updatedContractProducts)
    }
  }, [dealData])

  ////////////////////////////////// Deal Approvals /////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////////////////

  const handleRequestApproval = async (requestor, activeUser, approvers) => {
    console.log('[PROVIDER]activeUser', activeUser, 'approvers', approvers, 'requestor', requestor)

    const payload = createApproversNotification(newDeal, activeUser?.user?.id || activeUser?.id || '', approvers)

    const requestDataWithDealUpdates = getApprovalBatchReplaceItem(
      'ORGANIZATIONS_TABLE',
      newDeal?.PK,
      newDeal?.SK,
      requestor,
      activeUser?.user?.id || activeUser?.id || '',
      approvers,
      newDeal
    )

    try {
      // First: validate and save the deal
      const { PK, SK, deal_uuid } = newDeal
      const valid = validateFields(newDeal, productRows)
      if (!valid) {
        showSnackbar('Please fix validation errors before sending approval.', 'error')
        return
      }
      const dealUpdates = [...(requestDataWithDealUpdates ?? []), ...dealFieldChanges]
      console.log('payload', payload, 'dealUpdates', dealUpdates)

      await saveDealAsync({
        PK,
        SK,
        dealUpdates: dealUpdates,
        products: productRows,
        deletedProducts: deletedProductIds,
        deal_uuid: newDeal?.deal_uuid,
      })

      // Then: if save succeeded, send the notification
      sendApproverNotification(payload, {
        onSuccess: () => {
          showSnackbar('Request successfully sent', 'success')
        },
        onError: error => {
          showSnackbar('Notification failed to send.', 'error')
          console.error('Notification error:', error)
        },
      })
    } catch (error) {
      showSnackbar('Failed to save deal. Approval not requested.', 'error')
      console.error('Error during save & approval flow:', error)
    }
  }

  const resetState = () => {
    // Reset newDeal and also reset productRows to match originalDeal products
    setNewDeal({
      ...originalDeal,
      totals: {
        ...originalDeal.totals,
        totalGrossProfit: totalGrossProfit,
      },
      isApproved: originalDeal?.isApproved,
      isCompleted: originalDeal?.isCompleted,
      isCreated: originalDeal?.isCreated,
      isDraft: originalDeal?.isDraft,
      isInternal: originalDeal?.isInternal,
      isRequested: originalDeal?.isRequested,
    })
  }
  const onDragEnd = result => {
    // console.log('[onDragEnd] START: productRows: ', productRows)
    const { source, destination } = result
    // console.log('source: ', source, 'destination: ', destination)
    // If there's no destination, exit early
    if (!destination) return

    // If the item was dropped in the same place, exit early
    if (source.index === destination.index) return

    // Make a copy of the current rows and reorder them
    const updatedRows = Array.from(productRows)
    console.log('[onDragEnd] END productRows: ', productRows)
    const [movedItem] = updatedRows.splice(source.index, 1)
    console.log('[onDragEnd] movedItem: ', movedItem)
    updatedRows.splice(destination.index, 0, movedItem)

    // Update the state with reordered rows
    setProductRows(updatedRows)
    handleProductRowChange(originalProductRows, updatedRows)
  }
  ///////////////////////////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////////////////////

  const handleCreateTimelineData = (id, activeUser, action, type, details, message) => {
    const newTimelineData = {
      id: id,
      timeline_uuid: id,
      dataset: 'deals',
      user: {
        id: activeUser?.user?.id || activeUser?.id || '',
        name: activeUser?.real_name,
        image: activeUser?.profile?.image_512 || activeUser?.user?.profile?.image_512,
      },
      action: action,
      type: type,
      details: details,
      message: message,
    }
    console.log('newTimelineData', newTimelineData)
    setTimelineData(newTimelineData)
  }

  const handleCampaignChange = (campaignKey, updatedCampaign) => {
    console.log('campaignKey', campaignKey, 'updatedCampaign', updatedCampaign)
    const campaignType = campaignKey.split('-')[0]
    setStrategy(prevState => {
      if (campaignType === 'searchCampaigns') {
        const updatedPaidSearch = prevState?.paidSearch?.map(campaign =>
          campaign.key === campaignKey ? updatedCampaign : campaign
        )
        return { ...prevState, paidSearch: updatedPaidSearch }
      } else if (campaignType === 'socialCampaigns') {
        const updatedPaidSocial = prevState?.paidSocial?.map(campaign =>
          campaign.key === campaignKey ? updatedCampaign : campaign
        )
        return { ...prevState, paidSocial: updatedPaidSocial }
      } else if (campaignType === 'socialAds') {
        const updatedSocialAds = prevState?.socialAds?.map(campaign =>
          campaign.key === campaignKey ? updatedCampaign : campaign
        )
        return { ...prevState, socialAds: updatedSocialAds }
      }
      return prevState
    })
  }

  // useEffect(() => {
  //   if (!newDeal || !newDeal?.PK || !newDeal?.SK) return
  //   if (newDeal) {
  //     console.log('newDeal', newDeal)
  //   }
  // }, [newDeal])

  const handleStartEdit = () => {
    setIsEditMode(true)
  }
  const handleStopEdit = () => {
    setIsEditMode(false)
  }
  const handleOpenView = mode => {
    setViewMode(mode)
  }
  const handleCloseView = () => {
    setViewMode('strategy')
  }

  const handleDrawerOpen = () => {
    setDrawerOpen(true)
  }
  const handleDrawerClose = () => {
    setDrawerOpen(false)
  }
  const handleNcoDrawerOpen = () => {
    setNcoDrawerOpen(true)
  }
  const handleNcoDrawerClose = () => {
    setNcoDrawerOpen(false)
  }
  const handleGridDrawerOpen = () => {
    setIsGridDrawerOpen(true)
  }
  const handleGridDrawerClose = () => {
    setIsGridDrawerOpen(false)
  }
  const handleProductRowChange = (originalProducts, newProducts) => {
    // console.log('originalProducts', originalProducts)
    // console.log('newProducts', newProducts)
    const changes = getProductBatchReplaceItems(originalProducts, newProducts)
    // console.log('[handleProductRowChange] changes', changes)
    setProductRowChanges(changes)
  }
  useEffect(() => {
    // const updates = getDealFieldUpdates(originalDeal, newDeal)
    const updates = getDealBatchReplaceItems(originalDeal, newDeal)
    if (updates?.length > 0) console.log('updates', updates)
    setDealFieldChanges(updates)
  }, [newDeal, originalDeal])
  useEffect(() => {
    if (!relatedProducts || !relatedProducts.length || hasInitializedProducts.current) return

    const filtered = relatedProducts.filter(p => !deletedProductIds.includes(p.instanceId))
    const normalized = filtered.map(p => normalizeProductRowOnLoad(p, dealData?.dealStatus))

    setOriginalProductRows(JSON.parse(JSON.stringify(normalized)))
    setProductRows(normalized)
    hasInitializedProducts.current = true
  }, [relatedProducts, deletedProductIds])

  return (
    <DealContext.Provider
      value={{
        dealId,
        setDealId,
        newDeal,
        setNewDeal,
        productRows,
        setProductRows,
        originalApprovalState,
        approvalState,
        setApprovalState,
        isCreating,
        isError,
        handleUpdateDeal,
        createDocument,
        updateItems,
        validateFields,
        validationErrors,
        setValidationErrors,
        isValid,
        setIsValid,
        calculateMediaSpend,
        calculateTotalForRow,
        computeTotals,
        addRow,
        removeRow,
        handleProductChange,
        handleChange,
        handleRequestApproval,
        // handleApproveDeal,
        drawerOpen,
        handleDrawerOpen,
        handleDrawerClose,
        ncoDrawerOpen,
        handleNcoDrawerOpen,
        handleNcoDrawerClose,
        originalDeal,
        onDragEnd,
        //strategy//
        timelineData,
        handleCreateTimelineData,
        searchCampaigns,
        setSearchCampaigns,
        socialCampaigns,
        setSocialCampaigns,
        socialAds,
        setSocialAds,
        displayCampaigns,
        setDisplayCampaigns,
        // currentStrategy,
        // isStrategyLoading,
        // isStrategyError,
        currentStrategy,
        strategy,
        setStrategy,
        handleCampaignChange,
        handleStartEdit,
        handleStopEdit,
        isEditMode,
        setIsEditMode,
        viewMode,
        handleOpenView,
        handleCloseView,
        resetState,
        isDealStatusChange,
        setIsDealStatusChange,
        handleSaveAndSync,
        handleSaveOnly,
        dealFieldChanges,
        productRowChanges,
        deletedProductIds,
        totals,
        errorKeys,
        handleProductRowChange,
        clearChangeTracking,
        isGridDrawerOpen,
        handleGridDrawerOpen,
        handleGridDrawerClose,
        // findContactById,
      }}
    >
      {children}
    </DealContext.Provider>
  )
}
